import { useState, useEffect } from 'react';
import api from '../api';

/**
 * Smart data fetching hook for audit forms
 * Fetches auditors, scope, standards, and client info from backend
 * Minimizes manual input by auto-populating from project context
 */
export const useProjectAuditData = (projectId, projectMeta, stages) => {
  const [auditors, setAuditors] = useState([]);
  const [scope, setScope] = useState('');
  const [standards, setStandards] = useState('');
  const [client, setClient] = useState({});
  const [leadAuditor, setLeadAuditor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !projectMeta) return;

    setLoading(true);
    setError(null);

    Promise.all([
      // Fetch auditors from project schedules (leads and team members)
      api.get(`/audit/projects/${projectId}/schedules`)
        .then(res => {
          const schedules = res?.data?.data || [];
          const auditorSet = new Map();

          // Extract unique auditors from schedules
          schedules.forEach(schedule => {
            if (schedule.auditorId && schedule.auditorName) {
              auditorSet.set(schedule.auditorId, {
                id: schedule.auditorId,
                name: schedule.auditorName,
                role: schedule.leadId === schedule.auditorId ? 'Lead Auditor' : 'Auditor',
                isLead: schedule.leadId === schedule.auditorId,
              });
            }
            // Track lead auditor
            if (schedule.leadId && schedule.leadName) {
              if (!schedules.find(s => s.auditorId === schedule.leadId && s.auditorName === schedule.leadName)) {
                auditorSet.set(schedule.leadId, {
                  id: schedule.leadId,
                  name: schedule.leadName,
                  role: 'Lead Auditor',
                  isLead: true,
                });
              }
              setLeadAuditor({ id: schedule.leadId, name: schedule.leadName });
            }
          });

          return Array.from(auditorSet.values());
        })
        .catch(err => {
          console.warn('Failed to fetch auditors:', err);
          return [];
        }),

      // Fetch scope from proposal/stages
      Promise.resolve(
        stages?.map(s => s.serviceItem || s.stageName || '').filter(Boolean).join(', ') || ''
      ),

      // Fetch standards from EA codes
      Promise.resolve(
        stages?.map(s => s.eaCode || '').filter(Boolean).join(', ') || ''
      ),

      // Fetch client info
      Promise.resolve(projectMeta || {}),
    ])
      .then(([auditorsData, scopeData, standardsData, clientData]) => {
        setAuditors(auditorsData || []);
        setScope(scopeData);
        setStandards(standardsData);
        setClient(clientData);
      })
      .catch(err => {
        console.error('Error loading audit data:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [projectId, projectMeta, stages]);

  return {
    auditors,
    scope,
    standards,
    client,
    leadAuditor,
    loading,
    error,
  };
};
